import type { Product } from '../types/product';

/**
 * 商品类型 / 子类型 code 与展示名称映射（与编辑页选项保持一致）
 */

export const CATEGORY_MAP: Record<string, string> = {
  car_shop: '电动车商城',
  vip_rights: 'VIP权益',
};

export const CHILD_CATEGORY_MAP: Record<string, string> = {
  electric_bicycle: '电动自行车',
  electric_scooter: '电动滑板',
};

export const CHILD_CATEGORY_OPTIONS = Object.entries(CHILD_CATEGORY_MAP).map(([value, label]) => ({
  value,
  label,
}));

const lookupMap = (map: Record<string, string>, value?: string | null): string | undefined => {
  if (!value?.trim()) {
    return undefined;
  }
  const trimmed = value.trim();
  return map[trimmed] ?? map[trimmed.toLowerCase()];
};

/** 是否为枚举 code（如 electric_bicycle），而非中文展示名 */
const looksLikeEnumCode = (value: string) => /^[a-z][a-z0-9_]*$/i.test(value);

/**
 * 从列表/详情记录中读取子类型字段（兼容 camelCase / snake_case）
 */
export const pickChildCategoryFields = (
  record: Pick<Product, 'childCategory' | 'childCategoryDesc' | 'child_category' | 'child_category_desc'>
): { code?: string; desc?: string } => ({
  code: record.childCategory ?? record.child_category,
  desc: record.childCategoryDesc ?? record.child_category_desc,
});

/**
 * 解析子类型展示名：优先本地 code 映射；desc 与 code 相同时视为无效
 */
export const getChildCategoryLabel = (
  code?: string | null,
  desc?: string | null
): string => {
  const normalizedCode = code?.trim() || '';
  const normalizedDesc = desc?.trim() || '';

  const fromCode = lookupMap(CHILD_CATEGORY_MAP, normalizedCode);
  if (fromCode) {
    return fromCode;
  }

  const fromDesc = lookupMap(CHILD_CATEGORY_MAP, normalizedDesc);
  if (fromDesc) {
    return fromDesc;
  }

  if (normalizedDesc && normalizedDesc !== normalizedCode && !looksLikeEnumCode(normalizedDesc)) {
    return normalizedDesc;
  }

  return normalizedCode || '--';
};

/**
 * 解析商品类型展示名
 */
export const getCategoryLabel = (code?: string | null, desc?: string | null): string => {
  const normalizedCode = code?.trim() || '';
  const normalizedDesc = desc?.trim() || '';

  const fromCode = lookupMap(CATEGORY_MAP, normalizedCode);
  if (fromCode) {
    return fromCode;
  }

  const fromDesc = lookupMap(CATEGORY_MAP, normalizedDesc);
  if (fromDesc) {
    return fromDesc;
  }

  if (normalizedDesc && normalizedDesc !== normalizedCode && !looksLikeEnumCode(normalizedDesc)) {
    return normalizedDesc;
  }

  return normalizedCode || '--';
};
